import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { Project, ProjectWithId } from '../types/index';
import { db } from '../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';
import '../styles/Project.css';

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [project, setProject] = useState<ProjectWithId | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProject = async () => {
      if (!id) return;

      try {
        const docRef = doc(db, 'projects', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const projectData = { id: docSnap.id, ...(docSnap.data() as Project) } as ProjectWithId;
          setProject(projectData);
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <div className="project-container">
        <div className="loading">Loading project details...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-container">
        <div className="error-state">
          <h2>Project Not Found</h2>
          <p>The requested project could not be found.</p>
          <button onClick={() => navigate('/')} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-container">
      <header className="project-header">
        <div className="header-content">
          <button onClick={() => navigate('/')} className="back-button">
            ← Back to Dashboard
          </button>
          <div className="project-title-section">
            <h1>{project.name}</h1>
            <span className={`status-badge status-${project.status.replace(' ', '-')}`}>
              {project.status.replace('-', ' ')}
            </span>
          </div>
        </div>
      </header>

      <main className="project-content">
        <div className="project-overview">
          <div className="overview-card">
            <h2>Project Overview</h2>
            <div className="overview-grid">
              <div className="overview-item">
                <span className="label">Location:</span>
                <span className="value">{project.location}</span>
              </div>
              <div className="overview-item">
                <span className="label">Timber Type:</span>
                <span className="value">{project.timberType}</span>
              </div>
              <div className="overview-item">
                <span className="label">Square Footage:</span>
                <span className="value">{project.squareFootage.toLocaleString()} sq ft</span>
              </div>
              <div className="overview-item">
                <span className="label">Budget:</span>
                <span className="value">${project.budget.toLocaleString()}</span>
              </div>
              <div className="overview-item">
                <span className="label">Start Date:</span>
                <span className="value">{new Date(project.startDate).toLocaleDateString()}</span>
              </div>
              {project.endDate && (
                <div className="overview-item">
                  <span className="label">End Date:</span>
                  <span className="value">{new Date(project.endDate).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          <div className="progress-card">
            <h2>Progress</h2>
            <div className="progress-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${project.progress}%` }}
                ></div>
              </div>
              <span className="progress-text">{project.progress}% Complete</span>
            </div>
          </div>
        </div>

        <div className="project-details">
          <div className="description-card">
            <h2>Description</h2>
            <p>{project.description}</p>
          </div>

          {user?.role === 'employee' && project.clientIds && (
            <div className="clients-card">
              <h2>Assigned Clients</h2>
              <div className="clients-list">
                {project.clientIds.length > 0 ? (
                  project.clientIds.map((clientEmail: string, index: number) => (
                    <div key={index} className="client-item">
                      <span className="client-email">{clientEmail}</span>
                    </div>
                  ))
                ) : (
                  <p>No clients assigned</p>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProjectDetail;
